/*
  NB Logo Wall (sections/nbv2-logo-wall.liquid)

  The section has two modes, picked by data-mode on the root:

  carousel -- every logo is a slide in a Swiper instance (Swiper is loaded
  globally in layout/theme.liquid). Before it initialises, and if it never
  does, the stylesheet sizes the same markup as a native scroll-snap row.

  fade -- the row renders server-side with the first N logos already active,
  and on each tick the script picks a random cell, clones a random logo that
  is not currently on screen out of the hidden pool, and crossfades it in.
  Ticks are scheduled with a jittered setTimeout rather than setInterval so
  the rhythm is irregular.

  Both modes are correct with JS disabled, and both pause while the section
  is off-screen and (optionally) while the cursor is over the row.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-nbv2-logo-wall]';
  var ITEM_SELECTOR = '.nbv2-logo-wall__item';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Remove a faded-out node once its transition ends, with a timer fallback
     because transitionend never fires if the tab backgrounds mid-fade. */
  function removeWhenFaded(node, fadeMs) {
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      node.removeEventListener('transitionend', onTransitionEnd);
      if (node.parentNode) node.parentNode.removeChild(node);
    }

    function onTransitionEnd(event) {
      if (event.target === node && event.propertyName === 'opacity') finish();
    }

    node.addEventListener('transitionend', onTransitionEnd);
    setTimeout(finish, fadeMs + 100);
  }

  /* Carousel mode ------------------------------------------------------- */

  /* Mirrors the 989px / 749px rules in assets/section-nbv2-logo-wall.css. */
  var TABLET_MIN = 750;
  var DESKTOP_MIN = 990;
  var TABLET_GAP_SCALE = 0.7;
  var MOBILE_GAP_SCALE = 0.5;

  function initCarousel(root) {
    var container = root.querySelector('[data-nbv2-lw-carousel]');
    // No Swiper (blocked, failed to load): the CSS scroll-snap row stays.
    if (!container || typeof window.Swiper === 'undefined') return;

    var slideCount = container.querySelectorAll('.swiper-slide').length;
    if (!slideCount) return;

    var perView = Math.max(1, toNumber(root.getAttribute('data-per-view'), 5));
    var perViewTablet = Math.max(1, toNumber(root.getAttribute('data-per-view-tablet'), 3));
    var perViewMobile = Math.max(1, toNumber(root.getAttribute('data-per-view-mobile'), 3));
    var gap = Math.max(0, toNumber(root.getAttribute('data-gap'), 48));
    var delay = toNumber(root.getAttribute('data-swap-interval'), 3000);
    var speed = toNumber(root.getAttribute('data-speed'), 600);
    var byPage = root.getAttribute('data-step') === 'page';
    var wantsLoop = root.getAttribute('data-loop') === 'true';
    var reverse = root.getAttribute('data-reverse') === 'true';
    var pauseOnHover = root.getAttribute('data-pause-on-hover') === 'true';
    var autoplay = root.getAttribute('data-autoplay') === 'true' && !prefersReducedMotion();

    /*
      Swiper's loop rearranges real slides rather than cloning them, so it
      needs at least one logo more than is visible (its own check is
      slides >= slidesPerView + slidesPerGroup), and it pads uneven groups
      with blank slides. Loop is therefore only used for single-logo steps
      with a spare logo; everything else rewinds, which still lets autoplay
      cycle forever.
    */
    var loop = wantsLoop && !byPage && slideCount > perView;

    function view(count, gapScale) {
      return {
        slidesPerView: count,
        slidesPerGroup: byPage ? count : 1,
        spaceBetween: Math.round(gap * gapScale)
      };
    }

    var options = view(perViewMobile, MOBILE_GAP_SCALE);
    options.breakpoints = {};
    options.breakpoints[TABLET_MIN] = view(perViewTablet, TABLET_GAP_SCALE);
    options.breakpoints[DESKTOP_MIN] = view(perView, 1);
    options.speed = speed;
    options.loop = loop;
    options.rewind = !loop;
    options.grabCursor = true;
    // Fewer logos than cells: nothing to slide, so Swiper locks itself.
    options.watchOverflow = true;
    options.autoplay = autoplay
      ? {
          delay: delay,
          disableOnInteraction: false,
          pauseOnMouseEnter: pauseOnHover,
          reverseDirection: reverse
        }
      : false;

    var swiper = new window.Swiper(container, options);
    var observer = null;

    // Swiper pauses itself in a hidden tab, but not when scrolled out of view.
    if (autoplay && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(function (entries) {
        each(entries, function (entry) {
          if (!swiper.autoplay) return;
          if (entry.isIntersecting) swiper.autoplay.start();
          else swiper.autoplay.stop();
        });
      }, { rootMargin: '100px' });
      observer.observe(root);
    }

    root.nbv2LogoWallDestroy = function () {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (swiper && typeof swiper.destroy === 'function') swiper.destroy(true, true);
      swiper = null;
      root.nbv2LogoWallInit = false;
      root.nbv2LogoWallDestroy = null;
    };
  }

  /* Fade swap mode ------------------------------------------------------ */

  function initSection(root) {
    if (!root || root.nbv2LogoWallInit) return;
    root.nbv2LogoWallInit = true;

    if (root.getAttribute('data-mode') === 'carousel') {
      initCarousel(root);
      return;
    }

    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-nbv2-lw-slot]'));
    var pool = Array.prototype.slice.call(
      root.querySelectorAll('[data-nbv2-lw-pool] ' + ITEM_SELECTOR)
    );

    if (!slots.length) return;
    if (root.getAttribute('data-rotate') !== 'true') return;
    if (prefersReducedMotion()) return;
    // With no spare logos there is nothing distinct to swap in.
    if (pool.length <= slots.length) return;

    var interval = toNumber(root.getAttribute('data-swap-interval'), 3000);
    var jitter = toNumber(root.getAttribute('data-interval-jitter'), 0.3);
    var fade = toNumber(root.getAttribute('data-fade-duration'), 600);
    var perTick = Math.max(1, toNumber(root.getAttribute('data-swaps-per-tick'), 1));
    var pauseOnHover = root.getAttribute('data-pause-on-hover') === 'true';

    var state = {
      timer: null,
      running: false,
      visible: true,
      hovered: false,
      lastSlot: null,
      observer: null
    };

    function activeItem(slot) {
      return slot.querySelector(ITEM_SELECTOR + '.is-active');
    }

    function onScreenKeys() {
      var keys = {};
      each(slots, function (slot) {
        var item = activeItem(slot);
        if (item) keys[item.getAttribute('data-logo-key')] = true;
      });
      return keys;
    }

    /*
      Cells past the tablet/mobile count are display:none (the section emits
      those :nth-child rules from Liquid), so only the on-screen ones are
      worth swapping. Recomputed per swap because a resize changes the set.
      onScreenKeys() still scans every slot, hidden included, so a hidden
      cell's logo is never duplicated into a visible one.
    */
    function visibleSlots() {
      var shown = slots.filter(function (slot) {
        return slot.offsetParent !== null;
      });
      return shown.length ? shown : slots;
    }

    function pickSlot(available) {
      if (available.length < 2) return available[0];
      var slot = state.lastSlot;
      // Never swap the same cell twice in a row -- it reads as a flicker.
      while (slot === state.lastSlot) {
        slot = available[Math.floor(Math.random() * available.length)];
      }
      return slot;
    }

    function swapOne() {
      var used = onScreenKeys();
      var candidates = pool.filter(function (node) {
        return !used[node.getAttribute('data-logo-key')];
      });
      if (!candidates.length) return;

      var slot = pickSlot(visibleSlots());
      if (!slot) return;
      var outgoing = activeItem(slot);
      var incoming = candidates[Math.floor(Math.random() * candidates.length)].cloneNode(true);

      incoming.classList.remove('is-active', 'is-leaving');
      slot.appendChild(incoming);
      // Force a reflow so the browser transitions from the inactive state
      // instead of painting the node as already active.
      void incoming.offsetWidth;
      incoming.classList.add('is-active');

      if (outgoing) {
        outgoing.classList.remove('is-active');
        outgoing.classList.add('is-leaving');
        removeWhenFaded(outgoing, fade);
      }

      state.lastSlot = slot;
    }

    function nextDelay() {
      var factor = 1 + (Math.random() * 2 - 1) * jitter;
      return Math.max(200, interval * factor);
    }

    function clearTimer() {
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    }

    function schedule() {
      clearTimer();
      state.timer = setTimeout(tick, nextDelay());
    }

    function tick() {
      for (var i = 0; i < perTick; i++) swapOne();
      if (state.running) schedule();
    }

    function sync() {
      var shouldRun = state.visible && !state.hovered && !document.hidden;
      if (shouldRun && !state.running) {
        state.running = true;
        schedule();
      } else if (!shouldRun && state.running) {
        state.running = false;
        clearTimer();
      }
    }

    function onVisibilityChange() {
      sync();
    }

    function onEnter() {
      state.hovered = true;
      sync();
    }

    function onLeave() {
      state.hovered = false;
      sync();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    if (pauseOnHover) {
      root.addEventListener('mouseenter', onEnter);
      root.addEventListener('mouseleave', onLeave);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      state.visible = false;
      state.observer = new IntersectionObserver(function (entries) {
        each(entries, function (entry) {
          state.visible = entry.isIntersecting;
        });
        sync();
      }, { rootMargin: '100px' });
      state.observer.observe(root);
    }

    root.nbv2LogoWallDestroy = function () {
      state.running = false;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pauseOnHover) {
        root.removeEventListener('mouseenter', onEnter);
        root.removeEventListener('mouseleave', onLeave);
      }
      if (state.observer) {
        state.observer.disconnect();
        state.observer = null;
      }
      root.nbv2LogoWallInit = false;
      root.nbv2LogoWallDestroy = null;
    };

    sync();
  }

  function destroySection(root) {
    if (root && typeof root.nbv2LogoWallDestroy === 'function') root.nbv2LogoWallDestroy();
  }

  function walk(root, fn) {
    var scope = root || document;
    if (scope.matches && scope.matches(SELECTOR)) fn(scope);
    if (scope.querySelectorAll) each(scope.querySelectorAll(SELECTOR), fn);
  }

  function init(root) {
    walk(root, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    walk(event.target, destroySection);
  });
})();
